import { useTracking } from '@eventbrite/ads';
import {
    SegmentedCarousel,
    useSegmentedCarouselController,
} from '@eventbrite/collection-carousel';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { Typography } from '@eventbrite/marmalade';
import React, { useContext, useEffect, useState } from 'react';
import { TrackingContext } from '../../../contexts';
import { aggregateContext } from '../tracking';
import { OtherEventsYouMayLikeContentProps } from '../types';
import { getEventCardsMap } from './getEventCardsMap';
import styles from './OtherEventsYouMayLike.module.scss';
import { useOtherEventsYouMayLike } from './OtherEventsYouMayLikeData';
import './SegmentedCarousel.scss';
export type OtherEventsYouMayLikeProps = OtherEventsYouMayLikeContentProps;

const CAROUSEL_HEADING = gettext('Other events you may like');

const calculateTopOffset = () => {
    let isMobile = false;
    if (typeof window !== 'undefined') {
        isMobile = window?.innerWidth <= 960;
    }
    return isMobile;
};

export function OtherEventsYouMayLikeSegmentedCarousel({
    eventId,
    shareComponentProps,
    isAuthenticated,
    areMetricsEnabled,
    eventsToRemove,
    locale,
}: OtherEventsYouMayLikeProps) {
    const isMobile = calculateTopOffset();
    const segmentedCarouselControllerProps = useSegmentedCarouselController();
    const [events, setEvents] = useState<JSX.Element[]>([]);

    // Ads tracking
    const [trackingEvents, setTrackingEvents] = useState<FormattedEvent[]>([]);
    const context = useContext(TrackingContext);
    const { ref } = useTracking<HTMLDivElement>(({ id, event }) =>
        aggregateContext({
            id,
            event: { ...event, id },
            events: trackingEvents,
            context,
            page: {
                number: segmentedCarouselControllerProps.currentPage,
                size: 3,
            },
        }),
    );

    // Get related events
    const relatedFormattedEvents = useOtherEventsYouMayLike({
        eventId,
        isAuthenticated,
        suspense: true,
        eventsToRemove,
    });
    const relatedEvents = getEventCardsMap({
        events: relatedFormattedEvents,
        isAuthenticated,
        shareComponentProps,
        areMetricsEnabled,
        locale,
        bucketLabel: CAROUSEL_HEADING.toString(),
    });

    // Initially set events to Related Events
    useEffect(() => {
        if (relatedEvents.length && !events.length) {
            setEvents(relatedEvents);
            setTrackingEvents(relatedFormattedEvents);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={ref}
            className={styles.relatedEventsWrapper}
            data-testid="carousel"
        >
            <div className={styles.segmentedCarousel}>
                <SegmentedCarousel
                    {...{
                        ...segmentedCarouselControllerProps,
                        hideOverflow: !isMobile,
                        rootPaddingTop: 0,
                        rootPaddingBottom: 0,
                        rootPaddingLeft: isMobile ? 20 : 0,
                        rootPaddingRight: isMobile ? 20 : 0,
                        headerGap: 0,
                    }}
                >
                    <SegmentedCarousel.Header>
                        <Typography color="neutral-900" variant="heading-sm">
                            {CAROUSEL_HEADING.toString()}
                        </Typography>
                    </SegmentedCarousel.Header>
                    <SegmentedCarousel.Track>{events}</SegmentedCarousel.Track>
                    <SegmentedCarousel.Footer />
                </SegmentedCarousel>
            </div>
        </div>
    );
}
